import { useState } from "react";
import { NoRealData } from "@/components/shared";
import { CATEGORIAS_UC, GRUPOS_UC, TIPOS_AREA_VERDE } from "@/config/ambiental-carbono-config";

type TabKey = "areas-verdes" | "ucs" | "apps" | "recuperacao";

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
  emptyTitle: string;
  emptyDescription: string;
}

const TABS: TabDef[] = [
  {
    key: "areas-verdes",
    label: "Áreas Verdes",
    icon: "ri-landscape-line",
    emptyTitle: "Áreas verdes ainda não cadastradas",
    emptyDescription:
      "Aqui serão listadas praças, parques, bosques e demais áreas verdes com polígono, situação e exemplares arbóreos contidos. Ainda não há fonte real de dado para esta lista.",
  },
  {
    key: "ucs",
    label: "Unidades de Conservação",
    icon: "ri-shield-star-line",
    emptyTitle: "Unidades de Conservação ainda não cadastradas",
    emptyDescription:
      "Aqui serão listadas as UCs com categoria, grupo, órgão gestor, plano de manejo e documentos. Ainda não há fonte real de dado para esta lista.",
  },
  {
    key: "apps",
    label: "APPs Urbanas",
    icon: "ri-water-flash-line",
    emptyTitle: "APPs urbanas ainda não cadastradas",
    emptyDescription:
      "Aqui serão listadas as Áreas de Preservação Permanente urbanas (margens de cursos d'água, nascentes). Ainda não há fonte real de dado para esta lista.",
  },
  {
    key: "recuperacao",
    label: "Áreas de Recuperação",
    icon: "ri-heart-pulse-line",
    emptyTitle: "Áreas de recuperação ainda não cadastradas",
    emptyDescription:
      "Aqui serão acompanhadas as áreas em recuperação ambiental, com plantios e monitoramento. Ainda não há fonte real de dado para esta lista.",
  },
];

function VocabularyBlock({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <div>
      <div className="text-xs font-semibold text-foreground-700 mb-2">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className="px-2 py-0.5 bg-background-50 text-[11px] text-foreground-600 rounded-md border border-background-200"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Ambiental() {
  const [activeTab, setActiveTab] = useState<TabKey>("areas-verdes");
  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
          <a href="/" className="hover:text-primary-600 transition-colors">Dashboard</a>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <span className="text-foreground-700">Ambiental</span>
        </div>
        <h1 className="text-2xl font-bold text-foreground-900 font-heading">Ambiental</h1>
        <p className="text-sm text-foreground-500 mt-0.5">
          Áreas verdes, Unidades de Conservação, APPs urbanas e áreas de recuperação integradas ao CTA.
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-card border border-background-200 p-1.5 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap cursor-pointer ${
              activeTab === t.key
                ? "bg-primary-500 text-white shadow-sm"
                : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
            }`}
          >
            <i className={t.icon}></i>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <NoRealData variant="page" icon={tab.icon} title={tab.emptyTitle} description={tab.emptyDescription} />

      {/* Classificacao adotada (vocabulario de dominio, sem registros) */}
      {(activeTab === "areas-verdes" || activeTab === "ucs") && (
        <div className="bg-white rounded-card border border-background-200 p-4 space-y-4">
          <div className="text-sm font-semibold text-foreground-900">Classificação adotada</div>
          {activeTab === "areas-verdes" ? (
            <VocabularyBlock title="Tipos de área verde" items={TIPOS_AREA_VERDE} />
          ) : (
            <>
              <VocabularyBlock title="Grupos" items={GRUPOS_UC} />
              <VocabularyBlock title="Categorias" items={CATEGORIAS_UC} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
